import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  FiUsers,
  FiPlus,
  FiUpload,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiRefreshCw,
  FiX,
  FiFile,
  FiDownload,
  FiEdit,
} from "react-icons/fi";
import {
  fetchSurveyOfficers,
  createSurveyOfficer,
  uploadSurveyOfficers,
  downloadTemplate,
  updateSurveyOfficer,
} from "../../redux/actions/user-management/surveyOfficerActions";
import {
  fetchDistricts,
  fetchMunicipalities,
  fetchWards,
  clearMunicipalities,
  clearWards,
} from "../../redux/actions/internal/internalActions";

const SurveyOfficers = () => {
  const dispatch = useDispatch();
  const { loading, surveyOfficers, pagination, uploadProgress } = useSelector(
    (state) =>
      state.surveyOfficer || {
        loading: false,
        surveyOfficers: [],
        pagination: { page: 1, limit: 10, totalRecords: 0, totalPages: 0 },
        uploadProgress: 0,
      },
  );
  const { districts, municipalities, wards } = useSelector(
    (state) =>
      state.internal || { districts: [], municipalities: [], wards: [] },
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [filters, setFilters] = useState({
    district_id: "",
    municipality_id: "",
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [limit, setLimit] = useState(10);
  const limitOptions = [
    10, 20, 50, 100, 200, 500, 1000, 1500, 2000, 3000, 4000,
  ];
  const [phoneError, setPhoneError] = useState("");
  const [editPhoneError, setEditPhoneError] = useState("");
  const [editId, setEditId] = useState(null);
  const toggleWardExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    email: "",
    phone: "",
    district_id: "",
    municipality_id: "",
    ward_ids: [],
    role_id: 3,
  });
  const [editData, setEditData] = useState({
    name: "",
    designation: "",
    email: "",
    phone: "",
    district_id: "",
    municipality_id: "",
    ward_ids: [],
    role_id: 3,
  });
  const handleDownloadTemplate = () => {
    dispatch(downloadTemplate());
  };

  useEffect(() => {
    dispatch(fetchSurveyOfficers(currentPage, limit, filters));
    dispatch(fetchDistricts());
  }, [
    dispatch,
    currentPage,
    limit,
    filters.district_id,
    filters.municipality_id,
  ]);

  useEffect(() => {
    if (formData.district_id) {
      dispatch(fetchMunicipalities(formData.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [formData.district_id, dispatch]);

  useEffect(() => {
    if (filters.district_id) {
      dispatch(fetchMunicipalities(filters.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [filters.district_id, dispatch]);

  useEffect(() => {
    if (formData.municipality_id) {
      dispatch(fetchWards(formData.municipality_id));
    } else {
      dispatch(clearWards());
    }
  }, [formData.municipality_id, dispatch]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      dispatch(fetchSurveyOfficers(page, limit, filters));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchSurveyOfficers(currentPage, limit, filters));
    toast.success("Data refreshed!");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleWardSelect = (wardId) => {
    setFormData((prev) => ({
      ...prev,
      ward_ids: prev.ward_ids.includes(wardId)
        ? prev.ward_ids.filter((id) => id !== wardId)
        : [...prev.ward_ids, wardId],
    }));
  };
  const validatePhone = (value) => {
    return value.replace(/\D/g, "").slice(0, 10);
  };
  const isValidPhone = (phone) => {
    return phone.length === 10;
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(formData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    const submitData = {
      ...formData,
      district_id: parseInt(formData.district_id),
      municipality_id: parseInt(formData.municipality_id),
      role_id: 3,
    };

    try {
      const result = await dispatch(createSurveyOfficer(submitData));
      if (result?.meta?.status) {
        setShowAddModal(false);
        setFormData({
          name: "",
          designation: "",
          email: "",
          phone: "",
          district_id: "",
          municipality_id: "",
          ward_ids: [],
          role_id: 3,
        });
        dispatch(fetchSurveyOfficers(currentPage, 10));
      }
    } catch (error) {
      // Error handled in action
    }
  };

  const handleEdit = (officer) => {
    setEditId(officer.id);
    setEditData({
      name: officer.name || "",
      designation: officer.designation || "",
      email: officer.email || "",
      phone: officer.phone || "",
      district_id: officer.district_id || "",
      municipality_id: officer.municipality_id || "",
      ward_ids: officer.ward_ids || [],
      role_id: officer.role_id || 3,
    });
    if (officer.district_id) {
      dispatch(fetchMunicipalities(officer.district_id));
    }
    if (officer.municipality_id) {
      dispatch(fetchWards(officer.municipality_id));
    }
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(editData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    const submitData = {
      ...editData,
      district_id: parseInt(editData.district_id),
      municipality_id: parseInt(editData.municipality_id),
      role_id: 3,
    };

    try {
      const result = await dispatch(updateSurveyOfficer(editId, submitData));
      if (result?.meta?.status) {
        setShowEditModal(false);
        setEditData({
          name: "",
          designation: "",
          email: "",
          phone: "",
          district_id: "",
          municipality_id: "",
          ward_ids: [],
          role_id: 3,
        });
        dispatch(fetchSurveyOfficers(currentPage, 10, filters));
      }
    } catch (error) {
      // Error handled in action
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleEditWardSelect = (wardId) => {
    setEditData((prev) => ({
      ...prev,
      ward_ids: prev.ward_ids.includes(wardId)
        ? prev.ward_ids.filter((id) => id !== wardId)
        : [...prev.ward_ids, wardId],
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const result = await dispatch(uploadSurveyOfficers(formData));
      if (result?.meta?.status) {
        setSelectedFile(null);
        document.getElementById("uploadFileInput").value = "";
        setShowUploadModal(false);
        dispatch(fetchSurveyOfficers(currentPage, 10));
      }
    } catch (error) {
      // Error handled in action
    } finally {
      setIsUploading(false);
    }
  };

  const filteredOfficers = surveyOfficers;

  return (
    <div className="p-4 md:p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold uppercase text-gray-900 dark:text-white">
            Survey Officers
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage survey officers
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <FiUpload className="w-4 h-4" />
            Upload Officers
          </button>
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            <FiDownload className="w-4 h-4" />
            Download Template
          </button>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
          >
            <FiRefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Add Officer
          </button>
        </div>
      </div>

      {/* Officers List Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <FiUsers className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Officers List
            </h2>
            <span className="text-sm font-medium text-orange-800 dark:text-orange-200 border border-orange-200 dark:border-orange-700 px-3 py-1 rounded-md">
              TOTAL: {pagination.totalRecords || 0}
            </span>
            <div className="flex items-center gap-2 ml-4">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Show:
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border-2 border-orange-200 dark:border-orange-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-medium min-w-[80px]"
              >
                {limitOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={filters.district_id}
              onChange={(e) => {
                const value = e.target.value;
                setFilters({
                  district_id: value,
                  municipality_id: "",
                });
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">All Districts</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            <select
              value={filters.municipality_id}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  municipality_id: e.target.value,
                });
                setCurrentPage(1);
              }}
              disabled={!filters.district_id}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm disabled:opacity-50"
            >
              <option value="">All Municipalities</option>
              {municipalities.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            {(filters.district_id || filters.municipality_id) && (
              <button
                onClick={() => {
                  setFilters({
                    district_id: "",
                    municipality_id: "",
                  });
                  setCurrentPage(1);
                  dispatch(clearMunicipalities());
                }}
                className="px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm flex items-center gap-1"
              >
                <FiX className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>
        </div>
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {(pagination.page - 1) * limit + 1} to{" "}
              {Math.min(pagination.page * limit, pagination.totalRecords)} of{" "}
              {pagination.totalRecords} entries
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-400"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium">
                {currentPage}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-400"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-orange-600 dark:border-orange-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredOfficers.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      #
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Name
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Designation
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Email
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Phone
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      District
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Municipality
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Wards
                    </th>
                    <th className="text-left uppercase italic text-base py-3 px-4 text-sm font-medium text-orange-800 dark:text-orange-400">
                      Edit
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOfficers.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                        {(pagination.page - 1) * limit + index + 1}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                        {item.name || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.designation || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.email || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.phone || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.district_name || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.municipality_name || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        {item.ward_names
                          ? (() => {
                              const wardList = item.ward_names
                                .split(",")
                                .map((w) => w.trim());
                              const isExpanded = expandedRows[item.id];
                              const displayWards = isExpanded
                                ? wardList
                                : wardList.slice(0, 2);

                              return (
                                <div>
                                  <span className="font-mono text-xs">
                                    {displayWards.join(", ")}
                                    {wardList.length > 4 &&
                                      !isExpanded &&
                                      "..."}
                                  </span>
                                  {wardList.length > 4 && (
                                    <button
                                      onClick={() => toggleWardExpand(item.id)}
                                      className="ml-1 text-xs font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 transition-colors"
                                    >
                                      {isExpanded
                                        ? "Show less"
                                        : `+${wardList.length - 4} more`}
                                    </button>
                                  )}
                                </div>
                              );
                            })()
                          : "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-yellow-600 hover:text-orange-800 dark:text-yellow-400 dark:hover:text-orange-300 transition-colors"
                          title="Edit Survey Officer"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.totalRecords,
                  )}{" "}
                  of {pagination.totalRecords} entries
                </div>
                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Current Page Display */}
                  <span className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium">
                    {currentPage}
                  </span>

                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <FiUsers className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              No survey officers found
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Click "Add Officer" to create a new survey officer
            </p>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-white-100/30 backdrop-blur-sm">
          {" "}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Add Survey Officer
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      setFormData({ ...formData, phone: value });
                      if (value.length > 0 && value.length !== 10) {
                        setPhoneError("Phone number must be exactly 10 digits");
                      } else {
                        setPhoneError("");
                      }
                    }}
                    required
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      phoneError
                        ? "border-red-500 dark:border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                    placeholder="Enter 10-digit phone number"
                  />
                  {phoneError && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {phoneError}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    District *
                  </label>
                  <select
                    name="district_id"
                    value={formData.district_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Municipality *
                  </label>
                  <select
                    name="municipality_id"
                    value={formData.municipality_id}
                    onChange={handleInputChange}
                    required
                    disabled={!formData.district_id}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                  >
                    <option value="">Select Municipality</option>
                    {municipalities.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Wards *
                  </label>
                  {wards.length > 0 && formData.municipality_id && (
                    <button
                      type="button"
                      onClick={() => {
                        if (formData.ward_ids.length === wards.length) {
                          setFormData({ ...formData, ward_ids: [] });
                        } else {
                          setFormData({
                            ...formData,
                            ward_ids: wards.map((w) => w.id),
                          });
                        }
                      }}
                      className="flex items-center gap-1.5 text-xs font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={
                          formData.ward_ids.length === wards.length &&
                          wards.length > 0
                        }
                        onChange={() => {
                          if (formData.ward_ids.length === wards.length) {
                            setFormData({ ...formData, ward_ids: [] });
                          } else {
                            setFormData({
                              ...formData,
                              ward_ids: wards.map((w) => w.id),
                            });
                          }
                        }}
                        className="h-3.5 w-3.5 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                      {formData.ward_ids.length === wards.length
                        ? "Deselect All"
                        : "Select All"}
                    </button>
                  )}
                </div>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 max-h-32 overflow-y-auto bg-white dark:bg-gray-700">
                  {wards.length > 0 ? (
                    <div className="grid grid-cols-6 gap-1">
                      {wards.map((ward) => (
                        <label
                          key={ward.id}
                          className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 px-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={formData.ward_ids.includes(ward.id)}
                            onChange={() => handleWardSelect(ward.id)}
                            disabled={!formData.municipality_id}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {ward.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-2">
                      {!formData.municipality_id
                        ? "Select municipality first"
                        : "No wards available"}
                    </p>
                  )}
                </div>
                {formData.ward_ids.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Selected: {formData.ward_ids.length} ward(s)
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
                >
                  Create Officer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Upload Survey Officers
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  document.getElementById("uploadFileInput").value = "";
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-orange-500 dark:hover:border-orange-400 transition-colors">
              <input
                id="uploadFileInput"
                type="file"
                onChange={handleFileChange}
                className="hidden"
                disabled={isUploading || loading}
              />
              <label htmlFor="uploadFileInput" className="cursor-pointer block">
                <div className="flex flex-col items-center">
                  <FiFile className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-3" />
                  <p className="text-gray-600 dark:text-gray-300 font-medium">
                    {selectedFile ? selectedFile.name : "Click to select file"}
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    Supported formats: .xlsx, .xls, .csv
                  </p>
                  {selectedFile && (
                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-2">
                      Size: {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  )}
                </div>
              </label>
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div
                    className="bg-orange-600 dark:bg-orange-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {uploadProgress}% uploaded
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  document.getElementById("uploadFileInput").value = "";
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadSubmit}
                disabled={!selectedFile || isUploading || loading}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                    Uploading...
                  </>
                ) : (
                  "Upload"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Edit Survey Officer
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditData({
                    name: "",
                    designation: "",
                    email: "",
                    phone: "",
                    district_id: "",
                    municipality_id: "",
                    ward_ids: [],
                    role_id: 3,
                  });
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={editData.name}
                    onChange={handleEditInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={editData.designation}
                    onChange={handleEditInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={editData.email}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={editData.phone}
                    onChange={handleEditInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    District *
                  </label>
                  <select
                    name="district_id"
                    value={editData.district_id}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditData({
                        ...editData,
                        district_id: value,
                        municipality_id: "",
                        ward_ids: [],
                      });
                      if (value) {
                        dispatch(fetchMunicipalities(value));
                      } else {
                        dispatch(clearMunicipalities());
                      }
                    }}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Municipality *
                  </label>
                  <select
                    name="municipality_id"
                    value={editData.municipality_id}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditData({
                        ...editData,
                        municipality_id: value,
                        ward_ids: [],
                      });
                      if (value) {
                        dispatch(fetchWards(value));
                      } else {
                        dispatch(clearWards());
                      }
                    }}
                    required
                    disabled={!editData.district_id}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                  >
                    <option value="">Select Municipality</option>
                    {municipalities.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Wards *
                  </label>
                  {wards.length > 0 && editData.municipality_id && (
                    <button
                      type="button"
                      onClick={() => {
                        if (editData.ward_ids.length === wards.length) {
                          setEditData({ ...editData, ward_ids: [] });
                        } else {
                          setEditData({
                            ...editData,
                            ward_ids: wards.map((w) => w.id),
                          });
                        }
                      }}
                      className="flex items-center gap-1.5 text-xs font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={
                          editData.ward_ids.length === wards.length &&
                          wards.length > 0
                        }
                        onChange={() => {
                          if (editData.ward_ids.length === wards.length) {
                            setEditData({ ...editData, ward_ids: [] });
                          } else {
                            setEditData({
                              ...editData,
                              ward_ids: wards.map((w) => w.id),
                            });
                          }
                        }}
                        className="h-3.5 w-3.5 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                      {editData.ward_ids.length === wards.length
                        ? "Deselect All"
                        : "Select All"}
                    </button>
                  )}
                </div>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 max-h-32 overflow-y-auto bg-white dark:bg-gray-700">
                  {wards.length > 0 ? (
                    <div className="grid grid-cols-6 gap-1">
                      {wards.map((ward) => (
                        <label
                          key={ward.id}
                          className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 px-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={editData.ward_ids.includes(ward.id)}
                            onChange={() => handleEditWardSelect(ward.id)}
                            disabled={!editData.municipality_id}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {ward.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-2">
                      {!editData.municipality_id
                        ? "Select municipality first"
                        : "No wards available"}
                    </p>
                  )}
                </div>
                {editData.ward_ids.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Selected: {editData.ward_ids.length} ward(s)
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditData({
                      name: "",
                      designation: "",
                      email: "",
                      phone: "",
                      district_id: "",
                      municipality_id: "",
                      ward_ids: [],
                      role_id: 3,
                    });
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
                >
                  Update Officer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyOfficers;
